<template>
  <section class="px-2 mt-3">
    <div class="iq-card section-card">
      <div class="fms-section-header">
        <div>
          <h4>Main Engine, Auxiliary Engine & Power Meter Summary</h4>
          <p class="text-muted">
            24-hour summary of main engine run hours, average RPM, exhaust temperature, auxiliary engine, and power meter.
          </p>
        </div>
        <span class="fms-tag fms-tag--outline">24H Window</span>
      </div>
      <b-row class="summary-grid g-3 mx-0">
        <b-col cols="12" :lg="deviceStatus?.rpmTypesCount > 1 ? 7 : 5">
          <b-row>
            <b-col cols="12" :lg="deviceStatus?.rpmTypesCount > 1 ? 6 : 12" v-for="rpmType in deviceStatus?.rpmTypes || []" :key="rpmType">
              <EngineSummaryCard
                :title="isSingleMainEngine(currAsset.massId) ? 'MAIN ENGINE' : `${getRpmTypeLabel(rpmType, deviceStatus?.rpmTypes || [])} MAIN ENGINE`"
                :last-updated="currAssetSummary.rpm[rpmType].lastData"
                :rpm-chart="rpmChart"
                :rpm-series="getRpmSeries(rpmType)"
                :running-time="currAssetSummary.rpm[rpmType].runningTime"
                :average="currAssetSummary.rpm[rpmType].average"
                :show-exhaust="deviceStatus.hasExhaustTemperature"
                :exhaust="currAssetLastData?.temp?.port?.exhaust ?? '-'"
                element-id="chart-14-port"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="12" :lg="deviceStatus?.rpmTypesCount > 1 ? 5 : 7" class="ae-column">
          <b-row class="g-3">
            <b-col cols="12" class="mb-2" v-for="i in deviceStatus.aeCount" :key="i">
              <!-- <pre>{{ currAssetLastData.ae?.[i] }}</pre> -->
              <AuxiliaryEngineCard  v-if="i < 4" :title="`Auxiliary Engine ${i}`"
                :is-on="currAssetLastData.ae?.[i]?.rpm > 0"
                :running-seconds="currAssetSummary.ae[`ae${i}`] && currAssetSummary.ae[`ae${i}`].runningSeconds"
                :fuel-consumption="currAssetSummary?.ae[`ae${i}`] && currAssetSummary.ae[`ae${i}`].fuelConsumption"
                :is-have-power-meter="deviceStatus.hasPowermeter"
                :total-power="currAssetLastData.ae?.[i]?.totalPower"
                :total-energy="currAssetLastData.ae?.[i]?.totalEnergy"
                :frequency="currAssetLastData.ae?.[i]?.frequency"
                :voltageLtoL="currAssetLastData.ae?.[i]?.voltageLtoL"
                :voltageLtoN="currAssetLastData.ae?.[i]?.voltageLtoN"
                :current="currAssetLastData.ae?.[i]?.current"
                :power-factor="currAssetLastData.ae?.[i]?.powerFactor"
                :apparent-power="currAssetLastData.ae?.[i]?.apparentPower"
                 />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
// import { getRpmTypeLabel } from '@/util/fms-summary';
import EngineSummaryCard from './EngineSummaryCard.vue';
import AuxiliaryEngineCard from './AuxiliaryEngineCard.vue';

export default {
  name: 'EngineSummarySection',
  components: {
    EngineSummaryCard,
    AuxiliaryEngineCard,
  },
  props: {
    currAsset: {
      type: Object,
      default: null,
    },
    currAssetSummary: {
      type: Object,
      required: true,
    },
    currAssetLastData: {
      type: Object,
      default: null,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      rpmChart: {
        chart: {
          width: '100%',
          height: '100%',
          type: 'radialBar',
        },
        plotOptions: {
          hollow: {
            margin: 1,
            size: '70%',
            background: 'transparent',
            image: undefined,
            imageWidth: 22,
            imageHeight: 22,
          },
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            dataLabels: {
              name: {
                fontSize: '14px',
                color: undefined,
                offsetY: 110,
              },
              value: {
                offsetY: 70,
                fontSize: '18px',
                color: undefined,
                formatter(val) {
                  return ((val / 100) * 1600).toFixed(2);
                },
              },
            },
          },
        },
        fill: {},
        stroke: {
          dashArray: 4,
        },
        labels: ['Current RPM'],
        colors: ['#0084ff'],
      },
    };
  },
  computed: {
    ...mapGetters('asset', ['isSingleMainEngine']),
  },
  methods: {
    getRpmSeries(rpmType) {
      const rpm = this.currAssetLastData?.rpm && this.currAssetLastData.rpm[rpmType]
        ? this.currAssetLastData.rpm[rpmType].rpm
        : 0;

      return rpm ? Number(((rpm / 1600) * 100).toFixed(2)) : 0;
    },
  },
};
</script>

<style scoped>
.section-card {
  padding: 10px 10px 14px;
}

.summary-grid {
  padding: 0 10px 10px;
}

.ae-card {
  padding: 12px;
}

.ae-cons {
  font-size: 0.875rem;
  color: #6b7280;
}

.ae-title {
  font-size: 0.875rem;
  font-weight: 700;
  margin: 0;
}

.ae-hour {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 4px 0;
  line-height: 24px;
}

.ae-unit {
  font-size: 0.75rem;
}

.chart-title {
  font-size: 0.75rem;
  color: #2c3e50;
  margin: 0;
  text-align: center;
}

.chart-info {
  font-size: 1.125rem;
  color: #0084ff;
  margin: 0;
  text-align: center;
}

.chart-info-green {
  font-size: 1.5625rem;
  color: #55c95a;
  margin: 0;
}

@media screen and (max-width: 991px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .chart-title {
    font-size: 0.75rem;
  }

  .chart-info-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 5px;
    padding-left: 20px;
  }

  .rpm-chart-wrapper {
    display: none;
  }
}
</style>
